import { Box, Button, Flex, Stack, Text, VStack, useToast } from "@chakra-ui/react";
import { colord } from "colord";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import styled from "styled-components";
import LoadingIndicator from "../components/Loading";
import Modal from "../components/Modal";
import Chevron from "../components/icons/Chevron";
import { LoggedInLayout } from "../components/layouts/LoggedInLayout";
import { PageTitle } from "../components/text/PageTitle";
import { Job } from "@models/Job";
import { useCustomerUsersCustomers, useJobs } from "@api/hooks";
import { routes } from "../routes";
import { appTheme } from "@styles/Theme";
import SimpleSelect from "../components/ChakraUI/SimpleSelect";
import JobListData from "../components/jobList/JobListData";
import moment from "moment";
import { getErrorInFetchingData } from "@utils/utils";
import useStorage from "@utils/UseStorage";
import { useQueryClient } from "@tanstack/react-query";

const statusOptions = [
	{ key: "OPEN", value: "Ej bokad" },
	{ key: "BOOKED_WITH_UNMANNED_SHIFTS", value: "Delvis bokat" },
	{ key: "BOOKED", value: "Bokat" },
	{ key: "CANCELLED", value: "Avbrutet" },
	{ key: "BOOKED_&_END_DATE_IS_PREVIOUS_DATE", value: "Avslutat" },
	{ key: "DECLINED", value: "Nekat" },
];

const JobsPage = () => {
	const navigate = useNavigate();
	const toast = useToast();
	const { clear } = useStorage();
	const queryCache = useQueryClient();
	//  TODO: need to add pagination to each section if the list is longer than 10(adding view more to each section)
	const [page, setPage] = useState(1);
	const [ShowCreateOrderModal, setShowCreateOrderModal] = useState(false);
	const [selectedStatus, setSelectedStatus] = useState("");
	const [selectedCustomer, setSelectedCustomer] = useState("");
	const pageSize: number = 5;
	const shouldGroupData: boolean = true;
	// TODO : need to fix the issue when select BOOKED status (endDate issue)
	const endDataValue = selectedStatus === "BOOKED_&_END_DATE_IS_PREVIOUS_DATE" ? moment().format() : undefined;
	const getStatus = (status: string) => {
		if (status === "BOOKED_&_END_DATE_IS_PREVIOUS_DATE") return "BOOKED";
		return status;
	};
	const { customers, isLoading: isLoadingCustomers } = useCustomerUsersCustomers();
	const { data, isLoading, isFetching } = useJobs(
		page,
		pageSize,
		[getStatus(selectedStatus)] as Job["status"][],
		selectedCustomer,
		shouldGroupData,
		endDataValue,
		(code) => {
			if (code === "MUST_BE_LOGGED_IN") {
				clear();
				queryCache.clear();
				navigate(routes.signIn);
			}
			toast({
				title: getErrorInFetchingData(code),
				status: "error",
				duration: 9000,
				isClosable: true,
				variant: "subtle",
			});
		}
	);

	const handleCustomerSelection = (selectedCustomer: string) => {
		navigate(`${routes.jobCreate}?customer=${selectedCustomer}`);
	};

	const handleCreateOrder = () => {
		if (customers?.customers) {
			customers?.customers.length > 1
				? setShowCreateOrderModal(true)
				: handleCustomerSelection(customers?.customers[0].id);
		}
		return;
	};

	const customerOptions = customers?.customers?.map((customer) => ({ key: customer.id, value: customer?.name || "" })) || [];

	return (
		<LoggedInLayout>
			<VStack spacing={"1.5rem"} align={"flex-start"} w="full" pb={10}>
				<PageTitle showNotifications>Uppdrag</PageTitle>

				<Flex
					justify="space-between"
					w={"100%"}
					direction={{ base: "column-reverse", md: "row" }}
					gap={{ base: "1rem", md: "0" }}
					alignItems={"center"}
				>
					<Stack direction={{ base: "column", md: "row" }} spacing="4" alignItems={{ base: "center", md: "center" }}>
						{isLoadingCustomers && <LoadingIndicator size={"s"} />}
						{!isLoadingCustomers && customers && customers.customers && customers.customers.length > 1 && (
							<SimpleSelect
								fontSize={"16px"}
								width={250}
								value={selectedCustomer}
								onChange={(e) => setSelectedCustomer(e.target.value)}
								options={customerOptions}
								placeHolder="Alla enheter"
							/>
						)}
						{!isLoadingCustomers && (
							<SimpleSelect
								fontSize={"16px"}
								width={175}
								value={selectedStatus}
								onChange={(e) => setSelectedStatus(e.target.value)}
								options={statusOptions}
								placeHolder="Alla uppdrag"
							/>
						)}
					</Stack>
					<Button
						height="40px"
						width={"145px"}
						fontSize={"16px"}
						fontWeight={500}
						bg="#29A54D"
						_hover={{ opacity: 0.8 }}
						color="#FFFFFF"
						borderRadius={"full"}
						size="sm"
						onClick={handleCreateOrder}
					>
						Nytt uppdrag
					</Button>
				</Flex>

				{!isLoadingCustomers && <JobListData isLoading={isLoading || isFetching} jobData={data} />}

				<Modal show={ShowCreateOrderModal} onClose={() => setShowCreateOrderModal(false)}>
					<>
						<Text fontSize={20} fontWeight={600} align={"center"}>
							Åt vem beställer du?
						</Text>
						<Box maxH={"70vh"} overflowY={"auto"}>
							{customers?.customers?.map((c) => (
								<Choice
									key={c.id}
									onClick={() => handleCustomerSelection(c.id)}
									style={{ width: "100%", margin: "0 0 1rem 0" }}
								>
									{c.name}
									<Chevron color={appTheme.colors.brand} />
								</Choice>
							))}
						</Box>
					</>
				</Modal>
			</VStack>
		</LoggedInLayout>
	);
};

export default JobsPage;

const Choice = styled.div`
	border: 2px solid ${(props) => props.theme.colors.brand};
	background-color: ${(props) => colord(props.theme.colors.brand).lighten(0.5).toHex()};
	text-align: center;
	padding: 0.75rem 1rem;
	border-radius: 25px;
	font-weight: 500;
	font-size: 15px;
	position: relative;
	cursor: pointer;
	width: 50vw;
	max-width: 650px;
	width: 100%;
	margin: 1rem;

	transition: transform 0.125s;

	&:hover {
		background-color: ${(props) => colord(props.theme.colors.brand).lighten(0.45).toHex()};
		transform: scale(0.99);
	}
	svg {
		position: absolute;
		right: 1rem;
		top: 0.9rem;
	}

	&.active-choice {
		background-color: ${(props) => props.theme.colors.brand};
		color: white;
	}
`;
